import { useWorkflowFieldValues } from 'features/libra/fieldValues/useWorkflowFieldValues'
import { OntologyField } from 'features/libra/ontology/ontologyFieldTypes'
import { useOntologyFields } from 'features/libra/ontology/useOntologyFields'
import { isComputedSection } from 'features/libra/sections/computedSection/computedSectionTypes'
import { FormSection } from 'features/libra/sections/formSection/formSectionTypes'
import { SectionType } from 'features/libra/sections/sectionTypes'
import {
  LibraWorflowStepComponentProps,
  StepValidationResult,
  UseValidateConfigHook,
} from 'features/libra/workflow/workflowStepsApi'
import type { Dispatch, FC, SetStateAction } from 'react'
import React, { useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'

import { ComputedSectionView } from './conditionalForm/computed/ComputedSectionView'
import {
  NiiSaveBanner,
  StickySaveBannerContainer,
} from './niiReport/NiiSaveBanner'
import { NiiSeverityAndRiskCard } from './niiScreening/cards/NiiSeverityAndRiskCard'
import { NiiSuitabilityCard } from './niiScreening/cards/NiiSuitabilityCard'
import {
  NiiScreeningConfig,
  niiScreeningConfigSchema,
  NiiScreeningSection,
} from './niiScreening/niiScreeningApi'
import {
  NiiScreeningDataProvider,
  useNiiScreeningData,
} from './niiScreening/NiiScreeningDataContext'
import { NiiScreeningFormSection } from './niiScreening/NiiScreeningFormSection'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`

/**
 * Legacy content component - used when no sections are configured
 * Renders the hardcoded card layout for backwards compatibility
 */
const LegacyNiiScreeningContent: FC<{
  setValidationResult: Dispatch<SetStateAction<StepValidationResult>>
}> = ({ setValidationResult }) => {
  const { hasChanges, resetChanges, doSave, isSaving, isLoading } =
    useNiiScreeningData()

  useEffect(() => {
    if (hasChanges) {
      setValidationResult({
        message: 'Changes have been made to the NII screening form',
        status: 'Warning',
      })
    } else {
      setValidationResult(undefined)
    }
  }, [hasChanges, setValidationResult])

  return (
    <Container>
      <StickySaveBannerContainer>
        <NiiSaveBanner
          hasChanges={hasChanges}
          isLoading={isLoading}
          isSaving={isSaving}
          onReset={resetChanges}
          onSave={doSave}
        />
      </StickySaveBannerContainer>
      <NiiSuitabilityCard />
      <NiiSeverityAndRiskCard />
      {/* NiiScreeningOutcome removed - use config-driven approach instead */}
    </Container>
  )
}

interface SectionRendererProps {
  section: NiiScreeningSection
  fieldValues: Record<string, unknown>
  ontologyFields: Record<string, OntologyField>
  setFieldValues: Dispatch<SetStateAction<Record<string, unknown>>>
  sections: NiiScreeningSection[]
  onOverrideChange: (
    overrideField: string | undefined,
    justificationField: string | undefined
  ) => (
    overrideValue: boolean | undefined,
    justification: string | undefined
  ) => void
}

/**
 * Renders a single section based on its type (computed or form)
 */
const SectionRenderer: FC<SectionRendererProps> = ({
  section,
  fieldValues,
  ontologyFields,
  setFieldValues,
  sections,
  onOverrideChange,
}) => {
  if (isComputedSection(section)) {
    return (
      <ComputedSectionView
        config={section}
        fieldValues={fieldValues}
        ontologyFields={ontologyFields}
        onOverrideChange={onOverrideChange(
          section.overrideField,
          section.overrideJustificationField
        )}
        context={{
          ontologyFields,
          sections,
        }}
      />
    )
  }

  // Form section
  return (
    <NiiScreeningFormSection
      config={section}
      ontologyFields={ontologyFields}
      fieldValues={fieldValues}
      setFieldValues={setFieldValues}
    />
  )
}

/**
 * Config-driven content component - used when sections are configured
 * Renders sections based on the workflow JSON config
 */
const ConfigDrivenNiiScreeningContent: FC<{
  config: NiiScreeningConfig
  setValidationResult: Dispatch<SetStateAction<StepValidationResult>>
}> = ({ config, setValidationResult }) => {
  const { ontologyFields } = useOntologyFields()

  const sections = useMemo(() => config.sections ?? [], [config.sections])
  const form = useMemo(
    () =>
      config.form ?? sections.map((s) => ({ sectionId: s.id, showWhen: [] })),
    [config.form, sections]
  )

  // Convert sections to the format expected by useWorkflowFieldValues
  const formSections = useMemo(() => {
    return sections.filter((s): s is FormSection => s.type === SectionType.FORM)
  }, [sections])

  const {
    fieldValuesWithDefaults,
    setFieldValues,
    hasChanges,
    handleCancel,
    handleSave,
    isLoading,
  } = useWorkflowFieldValues({
    ontologyFields,
    sections: formSections,
    form,
  })

  // Handle override changes for computed sections
  const handleOverrideChange = useCallback(
    (
      overrideField: string | undefined,
      justificationField: string | undefined
    ) =>
      (
        overrideValue: boolean | undefined,
        justification: string | undefined
      ) => {
        setFieldValues((prev) => {
          // Start with filtered entries, removing override and justification fields
          const baseEntries = Object.entries(prev).filter(
            ([key]) => key !== overrideField && key !== justificationField
          )

          const updated = Object.fromEntries(baseEntries)

          // Add back the fields if they have values
          if (overrideField && overrideValue !== undefined) {
            updated[overrideField] = overrideValue
          }
          if (justificationField && justification !== undefined) {
            updated[justificationField] = justification
          }

          return updated
        })
      },
    [setFieldValues]
  )

  // Validate step completion
  useEffect(() => {
    if (hasChanges) {
      setValidationResult({
        message: 'Changes have been made to the NII screening form',
        status: 'Warning',
      })
    } else {
      setValidationResult(undefined)
    }
  }, [hasChanges, setValidationResult])

  return (
    <Container>
      <StickySaveBannerContainer>
        <NiiSaveBanner
          hasChanges={hasChanges}
          isLoading={isLoading}
          isSaving={isLoading}
          onReset={handleCancel}
          onSave={handleSave}
        />
      </StickySaveBannerContainer>
      {sections.map((section) => (
        <SectionRenderer
          key={section.id}
          section={section}
          fieldValues={fieldValuesWithDefaults}
          ontologyFields={ontologyFields}
          setFieldValues={setFieldValues}
          sections={sections}
          onOverrideChange={handleOverrideChange}
        />
      ))}
    </Container>
  )
}

export const useValidateConfig: UseValidateConfigHook = ({ config }) => {
  return useMemo(() => {
    const result = niiScreeningConfigSchema.safeParse(config)
    if (!result.success) {
      console.error('NiiScreening config validation failed:', result.error)
      console.error('Config that failed validation:', config)
    }
    return result.success
  }, [config])
}

const NiiScreeningComponent: FC<
  LibraWorflowStepComponentProps<NiiScreeningConfig>
> = ({ config, setValidationResult }) => {
  const parsed = useMemo(
    () => niiScreeningConfigSchema.safeParse(config),
    [config]
  )

  if (!parsed.success) {
    console.error(
      'NiiScreening Component: Failed to parse config:',
      parsed.error
    )
    console.error('Raw config:', config)

    return <div>Unable to parse NII config</div>
  }

  const niiConfig = parsed.data

  if (niiConfig.sections?.length) {
    return (
      <ConfigDrivenNiiScreeningContent
        config={niiConfig}
        setValidationResult={setValidationResult}
      />
    )
  }

  return (
    <NiiScreeningDataProvider niiConfig={niiConfig}>
      <LegacyNiiScreeningContent setValidationResult={setValidationResult} />
    </NiiScreeningDataProvider>
  )
}

NiiScreeningComponent.displayName = 'NiiScreeningComponent'

export const Component = React.memo(NiiScreeningComponent)
